import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FramerButton, TextInfoGroup } from "@/components/shared";
import { UsersPageModals } from "@/constants/enums";
import { useUserData } from "@/hooks";
import { useEditPasswordMutationHook, useEditUserMutationHook } from "@/hooks/mutations";
import { UserBasic } from "@/types";
import { useTranslation } from "react-i18next";
import { ChangeEvent, Dispatch, SetStateAction, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUserDataById } from "@/hooks/cache/useUserDataById";
import { useTheme } from "@/context/ThemeProvider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { handleValidation } from "@/utils/form";

type Props = {
  showModal: UsersPageModals;
  setShowModal: Dispatch<SetStateAction<UsersPageModals>>;
  userId: string;
  onClickDeleteUser: (_user: UserBasic) => void;
}

function ManageUserAdminModal({showModal, setShowModal, userId, onClickDeleteUser}: Props) {

  // States
  const [editPassword, setEditPassword] = useState<boolean>(false);
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [newPasswordState, setNewPasswordState] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  // Hooks
  const { t } = useTranslation(["modals", "tables", "common"]);
  const selectedUser = useUserDataById(userId) as UserBasic | null;
  const { darkMode } = useTheme();

  // Mutation hooks
  const loggedInUser = useUserData();
  const { editPasswordMutation } = useEditPasswordMutationHook();
  const { editUser } = useEditUserMutationHook();

  /**
   * Toggle activation status of user
   */
  function toggleActivation() {
    if (!selectedUser) return;
    editUser(selectedUser._id, selectedUser, { active: !selectedUser.active });

    setOpenDialog(false);
  }

  /**
   * OnClick handler for the edit password button
   * @param userObj
   */
  function onClickEditEmployer(userObj: UserBasic) {
    if(newPasswordState != "" && !hasErrors){
      editPasswordMutation({
        variables: {
          id: userObj._id,
          passwordInput: {
            newPassword: newPasswordState
          }
        },
      });
      setNewPasswordState("");
    }
  }

  /**
   * OnClick handler for the deactivate user button
   */
  function onClickDeactivateUser() {
    setOpenDialog(true);
  }

  /**
  * onChange handler for input fields
  * @param event: { name: string, value: string, isValid: boolean }
  */
  function handleOnChange(event: ChangeEvent<HTMLInputElement>, type: string) {
    const { name, value } = event.target;
    const isValid = handleValidation(value, type);

    if(isValid) {
      setHasErrors(false);
      if(name == "newPassword") {
        setNewPasswordState(value);
      }
    }
    else {
      setHasErrors(true);
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(UsersPageModals.NoModal);
    }
  };

  return (
    <Dialog open={showModal === UsersPageModals.ManageUser} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 max-w-xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="dark:text-white">{t("translation:manageusers.title")}</DialogTitle>
        </DialogHeader>

        {/* Main content */}
        {
          selectedUser &&
          <>
            <div className="flex-1 pt-2 overflow-y-auto">
              {/* Management Buttons */}
              {
                // Hide buttons for the logged in user
                loggedInUser?._id !== selectedUser._id &&
                <div className="flex gap-x-2 justify-end mb-4">
                  <FramerButton
                    className="bg-mforange text-white hover:bg-mforange/80"
                    onClick={onClickDeactivateUser}
                  >
                    {selectedUser.active ? t("common:buttons.deactivate"): t("common:buttons.activate")}
                  </FramerButton>
                  <FramerButton
                    className="bg-mforange text-white hover:bg-mforange/80"
                    onClick={() => onClickDeleteUser(selectedUser)}
                  >
                    {t("common:buttons.delete")}
                  </FramerButton>
                </div>
              }
              {/* User details */}
              <div className="flex flex-col gap-2 mb-4">
                {/* First and last name */}
                <div className="flex flex-wrap gap-2">
                  <TextInfoGroup label={t("tables:manageusers.tablecard.textinfogroups.firstname")} content={selectedUser.firstname} />
                  <TextInfoGroup label={t("tables:manageusers.tablecard.textinfogroups.lastname")} content={selectedUser.lastname} />
                </div>

                {/* Email */}
                <TextInfoGroup label={t("tables:manageusers.tablecard.textinfogroups.email")} content={selectedUser.mail} />
                {/* Employer ID */}
                {
                  selectedUser.employer &&
                  <TextInfoGroup className="md:whitespace-nowrap" label={t("tables:manageusers.tablecard.textinfogroups.employerid")} content={selectedUser.employer._id} />
                }
                {/* Company details */}
                <div className="flex flex-wrap gap-2">
                  <TextInfoGroup label={t("tables:manageusers.tablecard.tags.active")} content={selectedUser.active ? t("common:text.yes"): t("common:text.no")} />
                  {
                    selectedUser.employer &&
                    <TextInfoGroup label={t("tables:manageusers.tablecard.textinfogroups.company")} content={selectedUser.employer.name} />
                  }
                </div>
                {/* User details */}
                <div className="flex flex-wrap gap-2">
                  <TextInfoGroup label={t("tables:manageusers.tablecard.textinfogroups.usertype")} content={selectedUser.__typename} />
                  <TextInfoGroup label={t("tables:manageusers.tablecard.textinfogroups.role")} content={selectedUser.role} />
                </div>
                {
                  editPassword ?
                    <div className="px-1 mb-2">
                      <Label htmlFor="newPassword" className=" text-gray-400 text-sm select-none">{t("tables:manageusers.tablecard.textinfogroups.newpassword")}</Label>
                      <div className="flex items-center">
                        <div className="w-full">
                          <Input
                            autoComplete="new-password"
                            type="password"
                            required
                            name="newPassword"
                            onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event, "password")}
                            className={`dark:text-white text-gray-800 ${hasErrors ? "border-red-500 focus:border-red-500" : ""}`}
                            defaultValue={""}
                          />
                          {hasErrors && <p className="text-red-500">{t("tables:manageusers.tablecard.inputfields.passwd.errormsg")}</p>}
                        </div>
                      </div>
                    </div>
                    :
                    <TextInfoGroup className="h-[58px]" label={t("tables:manageusers.tablecard.textinfogroups.password")} content={"*****"} />
                }
                {
                  // Hide buttons for the logged in user
                  loggedInUser?._id !== selectedUser._id &&
                    <div className="flex gap-2 justify-between">
                      <FramerButton
                        variant="outline-primary"
                        onClick={() => setEditPassword((prev) => !prev)}
                      >
                        {editPassword ? t("common:buttons.cancel"): t("tables:manageusers.tablecard.buttons.editpassword")}
                      </FramerButton>
                      {
                        editPassword &&
                        <FramerButton
                          onClick={() => onClickEditEmployer(selectedUser)}
                          className="bg-mforange text-white hover:bg-mforange/80"
                        >
                          {t("common:buttons.save")}
                        </FramerButton>
                      }
                    </div>
                }
              </div>
            </div>

            <DialogFooter>
              {/* Action Buttons */}
              <div className="w-full">
                <FramerButton
                  variant="outline-primary"
                  onClick={() => {
                    setShowModal(UsersPageModals.NoModal);
                  }}
                >
                  {t("common:buttons.close")}
                </FramerButton>
              </div>
            </DialogFooter>

            <AlertDialog open={openDialog}>
              <AlertDialogContent className={`${darkMode ? "bg-mfdarklight border-mfdarkbase text-white" : "bg-white"}`}>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {
                      t("manageusers.dialog.userActivation.title",
                        { toggle: selectedUser.active ? t("manageusers.dialog.userActivation.deactivate") : t("manageusers.dialog.userActivation.activate") }
                      )
                    }
                  </AlertDialogTitle>
                  <AlertDialogDescription className={`${darkMode ? " text-gray-200" : "text-gray-800"}`}>
                    {
                      t("manageusers.dialog.userActivation.subtitle",
                        { toggle: (selectedUser.active ? t("manageusers.dialog.userActivation.deactivate") : t("manageusers.dialog.userActivation.activate")).toLowerCase() }
                      )
                    } &nbsp;
                    <span className="font-semibold">{selectedUser?.firstname} {selectedUser?.lastname}</span>.
                    <br />{t("manageusers.dialog.userActivation.note")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <FramerButton
                    variant="outline-primary"
                    onClick={() => setOpenDialog(false)}
                  >
                    {t("common:buttons.cancel")}
                  </FramerButton>

                  <FramerButton
                    onClick={() => toggleActivation()}
                    className="bg-mforange hover:bg-mforange hover:opacity-90"
                  >
                    {selectedUser.active ? t("manageusers.dialog.userActivation.deactivate") : t("manageusers.dialog.userActivation.activate")}
                  </FramerButton>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        }
      </DialogContent>
    </Dialog>
  );
}

export default ManageUserAdminModal;
